import React from "react";

import { Text, Tooltip } from "@shopify/polaris";
import type { EditablePurchaseOrderLineItem, IPurchaseOrder } from "../../../api_utils/types";
import { formattedSalesRate } from "../../../helper_functions/utils";
import MinOrMinimumOrderQuantityCalculation from "./min_or_minimum_order_quantity_calculation";

const recommendationsSkeleton = (
  <Tooltip content="Please wait while we finish importing your store">
    <span>-</span>
  </Tooltip>
);

export default function NiceSalesRateRecommendation({
  purchaseOrderLineItem,
  purchaseOrder,
  readyForRecommendations,
}: {
  readonly purchaseOrderLineItem: EditablePurchaseOrderLineItem;
  readonly purchaseOrder: IPurchaseOrder;
  readonly readyForRecommendations: boolean;
}): React.JSX.Element | string {
  if (!readyForRecommendations) {
    return recommendationsSkeleton;
  }

  const { arrival_date, days_of_inventory } = purchaseOrder;
  const {
    currently_available,
    sales_rate,
    relevant_purchase_order_quantity,
    recommended_quantity,
    needed,
    pack_size
  } = purchaseOrderLineItem;
  let calculation: React.ReactNode;
  if (recommended_quantity > 0 && recommended_quantity !== needed) {
    calculation = <MinOrMinimumOrderQuantityCalculation purchaseOrderLineItem={purchaseOrderLineItem} />;
  } else {
    const pre_ordering_days = currently_available > 0 ? (arrival_date.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24) : 0;
    const depletion_date = new Date(arrival_date.getTime() + days_of_inventory * 24 * 60 * 60 * 1000);
    const ordering_days = (depletion_date.getTime() - arrival_date.getTime()) / (1000 * 60 * 60 * 24);
    const total_days = Math.round(pre_ordering_days + ordering_days);
    const recommended_quantity_estimate = Math.round(sales_rate * total_days);
    const nice_sales_rate = formattedSalesRate({ sales_rate });
    calculation = (
      <>
        <Text as="strong">Est. calculation</Text>
        <Text as="p">&nbsp;&nbsp;&nbsp;{nice_sales_rate} sales rate</Text>
        <Text as="p">x {total_days} days of inventory</Text>
        <Text as="p">= {recommended_quantity_estimate} needed</Text>
        <br />
        <Text as="p">&nbsp;&nbsp;&nbsp;{recommended_quantity_estimate} needed</Text>
        <Text as="p">- {currently_available} currently available</Text>
        <Text as="p">- {relevant_purchase_order_quantity || 0} ordered</Text>
        <Text as="p">= {recommended_quantity} recommended</Text>
        {pack_size ? <><br /><Text as="p">{pack_size} Pack size</Text></> : null}
        <br />
        <Text as="p">Note: Exact recommendations may vary based on other factors.</Text>
      </>
    );
  }

  return <Tooltip content={calculation} preferredPosition="above">
    <Text as="strong">{recommended_quantity}</Text>
  </Tooltip>;
}
